import { Spacing } from '@persona-id/design-tokens';
import { CircleAlertIcon } from '@persona-id/design-tokens/icons';

import useIsITEInternal from 'dashboard/apps/InquiryTemplateEditor/hooks/useIsITEInternal';
import { COMPONENT_FIELD_DEFAULTS } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/constants';
import {
  ScreenEditorComponent,
  SelectedComponentAndStep,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/types';
import CodeEditor from 'dashboard/components-lib/CodeEditor';
import Banner from 'design-system/components/Banner/Banner';
import { isUiFormFieldComponentConfig } from 'lib/interfaces/ui-components';
import { captureMessage } from 'lib/reporting';

type WarningDataForSentry = Record<string, unknown>;

type FallbackAlertBlockProps = {
  title: string;
  sentryWarningTitle: string;
  sentryWarningData?: WarningDataForSentry;
};

const FallbackAlertBlock = ({
  title,
  sentryWarningTitle,
  sentryWarningData,
}: FallbackAlertBlockProps) => {
  const isITEInternal = useIsITEInternal();

  if (sentryWarningData) {
    captureMessage(sentryWarningTitle, 'warning', sentryWarningData);
  }

  return (
    <>
      <Banner variant='warning' style={{ paddingTop: Spacing[3], paddingBottom: Spacing[3] }}>
        <CircleAlertIcon width={16} />
        {title}
        {isITEInternal && sentryWarningData && (
          <>
            <br />
            Sentry debug information below
          </>
        )}
      </Banner>
      {/** Internal debugging */}
      {isITEInternal && sentryWarningData && (
        <CodeEditor
          code={JSON.stringify(sentryWarningData, null, 2)}
          completionSources={{}}
          language='json'
          readOnly
        />
      )}
    </>
  );
};

export enum ComponentPanelFallbackCondition {
  ComponentLibrary = 'componentLibrary',
  FieldSchema = 'fieldSchema',
}

const fallbackStrings: Record<
  ComponentPanelFallbackCondition,
  Pick<FallbackAlertBlockProps, 'title' | 'sentryWarningTitle'>
> = {
  [ComponentPanelFallbackCondition.ComponentLibrary]: {
    title: 'This component is currently not supported for editing in screen editor.',
    sentryWarningTitle: 'Attempted to edit unsupported component type in Screen editor',
  },
  [ComponentPanelFallbackCondition.FieldSchema]: {
    title: 'Field editing is currently unavailable for this component',
    sentryWarningTitle: 'Field schema data is missing for component in Screen editor',
  },
};

type GetComponentPanelFallbackReturn = {
  type: ComponentPanelFallbackCondition;
  screen: React.ReactElement<FallbackAlertBlockProps>;
};

const getComponentPanelFallback = ({
  selectedComponentAndStep,
  componentScreenEditorData,
}: {
  selectedComponentAndStep: SelectedComponentAndStep;
  componentScreenEditorData?: ScreenEditorComponent;
}): GetComponentPanelFallbackReturn => {
  const missingRequiredFieldSchemaData =
    isUiFormFieldComponentConfig(selectedComponentAndStep?.component) &&
    COMPONENT_FIELD_DEFAULTS[selectedComponentAndStep.component.type] == null;

  const componentLibraryEntryMissing = componentScreenEditorData == null;

  let fallbackCondition = null;

  // The missing component library entry error is higher priority
  // so always default to it if it is true
  if (missingRequiredFieldSchemaData) {
    fallbackCondition = ComponentPanelFallbackCondition.FieldSchema;
  } else if (componentLibraryEntryMissing) {
    fallbackCondition = ComponentPanelFallbackCondition.ComponentLibrary;
  }

  if (fallbackCondition != null) {
    return {
      type: fallbackCondition,
      screen: (
        <FallbackAlertBlock
          {...fallbackStrings[fallbackCondition]}
          sentryWarningData={{
            type: ComponentPanelFallbackCondition.ComponentLibrary,
            editorData: {
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              screen: selectedComponentAndStep.step.name,
              component: {
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                name: selectedComponentAndStep.component.name,
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                type: selectedComponentAndStep.component.type,
              },
            },
          }}
        />
      ),
    };
  }

  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  return null;
};

export default getComponentPanelFallback;
